"use client";

import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import Image from "next/image";
import { createPortal } from "react-dom";
import { useBoundStore } from "@/lib/stores/useBoundStore";
import { useAuthStore } from "@/lib/stores/useAuthStore";
import { useGetAllOrgs } from "@/lib/hooks/useGetAllOrgs";
import Chevron from "@/assets/icons/Chevron.svg";
import DePuySynthesLogo from "@/assets/logos/DePuySynthes.svg";
import DefaultOrgIcon from "@/assets/logos/DefaultOrgIcon.svg";
import { useMedia } from "@/lib/hooks/useMedia";

//#region Styled Components
const SwitcherContainer = styled.div<{
  $isClickable: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 15px;
  background-color: #fff;
  cursor: ${(props) => (props.$isClickable ? "pointer" : "default")};
  max-width: 500px;
  position: relative;
  transition: all 0.2s;

  @media (max-width: 700px) {
    max-width: 300px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: 12px;
  min-width: 150px;

  &.viewing-enrollment {
    img,
    .selected-name {
      opacity: 50%;
    }
  }

  .selected-name {
    display: flex;
    align-items: center;
    gap: 5px;
    flex: 1;
    font-size: 1.25rem;
    min-width: 0;

    span {
      color: black;
      font-weight: 300;
      flex-shrink: 0;
    }

    .org {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }
  }

  @media (max-width: 1024px) {
    padding: 10px 20px;
  }
`;

const ChevronImg = styled.img<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  width: 16px;
  height: 10px;
  flex-shrink: 0;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

const DropdownPortal = styled.div<{
  $top: number;
  $left: number;
  $width: number;
}>`
  position: fixed;
  top: ${(props) => props.$top}px;
  left: ${(props) => props.$left}px;
  width: ${(props) => props.$width}px;
  background: #fff;
  border-radius: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 5px;

  .org-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      color: ${(props) => props.theme.main};
    }

    &:first-child {
      border-radius: 8px 8px 0 0;
    }

    &:last-child {
      border-radius: 0 0 8px 8px;
    }
  }

  .org-name {
    font-size: 0.875rem;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;

    &.selected-org {
      color: ${(props) => props.theme.blue};
      font-weight: bold;
    }
  }

  .org-icon {
    width: 25px;
    height: 25px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  @media (max-width: 1024px) {
    .org-name {
      font-size: 1rem;
    }
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const SkeletonSpan = styled.span<{ width?: string; height?: string }>`
  display: inline-block;
  height: ${({ height }) => height || "30px"};
  width: ${({ width }) => width || "150px"};
  border-radius: 5px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 400% 100%;
  animation: ${pulse} 1.4s ease infinite;
`;
//#endregion

export default function MultiOrgDropdown() {
  const selectedOrgId = useBoundStore((state) => state.selectedOrgId);
  const setSelectedOrgId = useBoundStore((state) => state.setSelectedOrgId);
  const user_profile = useAuthStore((state) => state.user_profile);
  const max1024w = useMedia("(max-width: 1024px)");
  const patientEnrollment = useBoundStore((state) => state.patientEnrollment);

  const { orgOptions, isLoading } = useGetAllOrgs();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentSectionRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null); // Add ref for dropdown portal
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  // Initialize selectedOrgId with first org if not set
  useEffect(() => {
    if (
      !selectedOrgId &&
      user_profile?.orgid &&
      user_profile.orgid.length > 0
    ) {
      setSelectedOrgId(user_profile.orgid[0]);
    }
  }, [selectedOrgId, user_profile, setSelectedOrgId]);

  useEffect(() => {
    function updatePosition() {
      if (contentSectionRef.current) {
        const refValues = contentSectionRef.current.getBoundingClientRect();
        setDropdownPos({
          top: refValues.bottom,
          left: refValues.left,
          width: refValues.width,
        });
      }
    }

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside - FIXED
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside BOTH the container AND the dropdown
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleOrgSelect = (orgId: string) => {
    console.log("Selected org ID:", orgId);
    setSelectedOrgId(orgId);
    setIsOpen(false);
  };

  // Find selected org
  const selectedOrg = orgOptions.find((org) => org.id === selectedOrgId);
  const hasMultipleOrgs = orgOptions.length > 1;

  return (
    <>
      <SwitcherContainer
        $isClickable={hasMultipleOrgs && !patientEnrollment}
        className=""
        ref={containerRef}
        onClick={
          hasMultipleOrgs && !patientEnrollment
            ? () => setIsOpen(!isOpen)
            : undefined
        }
        onKeyDown={hasMultipleOrgs ? handleKeyDown : undefined}
        tabIndex={hasMultipleOrgs ? 0 : -1}
        role={hasMultipleOrgs ? "button" : undefined}
        aria-haspopup={hasMultipleOrgs ? "listbox" : undefined}
        aria-expanded={hasMultipleOrgs ? isOpen : undefined}
      >
        {!max1024w && (
          <Image
            src={DePuySynthesLogo.src}
            alt="DePuy Synthes"
            width={290}
            height={44}
            style={{ height: "44px", width: "auto", paddingLeft: "30px" }}
          />
        )}
        {!isLoading ? (
          <ContentSection
            ref={contentSectionRef}
            className={
              hasMultipleOrgs && patientEnrollment ? "viewing-enrollment" : ""
            }
          >
            <div className="selected-name johnson-display">
              {!max1024w && <span>-</span>}
              <div className="org" title={selectedOrg?.name}>
                {selectedOrg?.name}
              </div>
            </div>
            {hasMultipleOrgs && (
              <ChevronImg
                $isOpen={isOpen}
                src={Chevron.src}
                alt="toggle"
                width={16}
                height={10}
              />
            )}
          </ContentSection>
        ) : (
          <SkeletonSpan />
        )}
      </SwitcherContainer>

      {isOpen &&
        createPortal(
          <DropdownPortal
            ref={dropdownRef}
            $top={dropdownPos.top}
            $left={dropdownPos.left}
            $width={dropdownPos.width}
            role="listbox"
          >
            {orgOptions.map((org) => (
              <div
                className="org-option"
                key={org.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOrgSelect(org.id);
                }}
                role="option"
                aria-selected={org.id === selectedOrgId}
              >
                <div className="org-icon">
                  {org.logo ? (
                    <img src={org.logo} alt={org.name} />
                  ) : (
                    <img src={DefaultOrgIcon.src} alt="default" />
                  )}
                </div>
                <span
                  className={`org-name ${org.id === selectedOrgId && "selected-org"}`}
                  title={org.name}
                >
                  {org.name}
                </span>
              </div>
            ))}
          </DropdownPortal>,
          document.body
        )}
    </>
  );
}
